import React, { useEffect, useState } from 'react'
import {
  Alert, Badge, Box, Button, Chip, CircularProgress, Typography,
} from '@mui/material'
import AssignmentIcon       from '@mui/icons-material/AssignmentOutlined'
import EventAvailableIcon   from '@mui/icons-material/EventAvailableOutlined'
import HowToRegIcon         from '@mui/icons-material/HowToRegOutlined'
import RestartAltIcon       from '@mui/icons-material/RestartAltOutlined'
import ChevronRightIcon     from '@mui/icons-material/ChevronRight'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../supabaseClient'
import { useAuth }  from '../../auth/AuthContext'
import AttendanceDialog from '../classes/AttendanceDialog'

// ── Types ─────────────────────────────────────────────────────────────────────

interface TopicSubmissionItem {
  project_id:                  string
  student_id:                  string
  student_name:                string
  finalist_count:              number
  finalist_titles:             string[]
  finalist_recommendation_ids: string[]
  student_thoughts_excerpt:    string
  submitted_at:                string
}

interface RsvpItem {
  meeting_id:    string
  class_id:      string
  class_name:    string
  scheduled_at:  string
  rsvp_count:    number
}

interface UnmarkedMeetingItem {
  meeting_id:   string
  class_id:     string
  class_name:   string
  kind:         'group' | 'office_hours'
  scheduled_at: string
  enrolled_count: number
}

interface UnreadReplyItem {
  project_id:    string
  student_id:    string
  student_name:  string
  excerpt:       string
  arrived_at:    string
}

interface Step2DraftItem {
  project_id:    string
  student_id:    string
  student_name:  string
  draft_title:   string  // first 100 chars of question or problem statement
  submitted_at:  string
  is_eng:        boolean
}

interface Step3SubmissionItem {
  project_id:       string
  student_id:       string
  student_name:     string
  refined_question: string  // first 100 chars, from payload
  submitted_at:     string
}

interface Step4SubmissionItem {
  project_id:   string
  student_id:   string
  student_name: string
  hypothesis:   string  // first 100 chars of the primary hypothesis
  submitted_at: string
}

interface Step5SubmissionItem {
  project_id:   string
  student_id:   string
  student_name: string
  design_title: string  // first 100 chars of design type (sci) / design description (eng)
  submitted_at: string
  is_eng:       boolean
}

interface Step6SubmissionItem {
  project_id:   string
  student_id:   string
  student_name: string
  observation:  string  // first 100 chars of the preliminary observation
  submitted_at: string
}

interface RevisionRequestItem {
  request_id:    string
  project_id:    string
  student_id:    string
  student_name:  string
  step_number:   number
  reason:        string
  requested_at:  string
}

interface ScheduledMentorMeeting {
  meeting_id:   string
  project_id:   string | null
  student_id:   string
  student_name: string
  scheduled_at: string | null   // null while status is 'requested'
  status:       'requested' | 'scheduled'
  note:         string | null
  kind:         'kickoff' | 'step1_review' | 'parent_requested' | 'other'
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDateTime(s: string) {
  return new Date(s).toLocaleString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
    timeZone: 'America/Los_Angeles',
  })
}
function timeSince(s: string) {
  const days = Math.floor((Date.now() - new Date(s).getTime()) / 864e5)
  if (days <= 0) return 'today'
  if (days === 1) return '1 day ago'
  return `${days} days ago`
}

// ── Action row primitive ──────────────────────────────────────────────────────

function ActionRow({
  primary, secondary, action, onAction, danger,
}: {
  primary:   React.ReactNode
  secondary?: React.ReactNode
  action:    string
  onAction:  () => void
  danger?:   boolean
}) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
      gap: 1.5, py: 1.25,
      borderTop: '1px solid', borderColor: 'divider',
      '&:first-of-type': { borderTop: 'none' },
    }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{primary}</Typography>
        {secondary && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {secondary}
          </Typography>
        )}
      </Box>
      <Button
        size="small"
        variant={danger ? 'contained' : 'outlined'}
        color={danger ? 'warning' : 'primary'}
        endIcon={<ChevronRightIcon fontSize="small" />}
        onClick={onAction}
        sx={{ flexShrink: 0 }}
      >
        {action}
      </Button>
    </Box>
  )
}

// ── Section header ────────────────────────────────────────────────────────────

function SectionHeader({ icon, title, count, color }: {
  icon: React.ReactNode; title: string; count: number; color: 'error' | 'warning' | 'info'
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, mt: 1.5, '&:first-of-type': { mt: 0 } }}>
      {icon}
      <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>{title}</Typography>
      <Chip size="small" label={count} color={color} sx={{ minWidth: 32, fontWeight: 700 }} />
    </Box>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ActionItemsPanel() {
  const { session, role } = useAuth()
  const navigate          = useNavigate()
  const [submissions, setSubmissions]      = useState<TopicSubmissionItem[]>([])
  const [step2Drafts,  setStep2Drafts]     = useState<Step2DraftItem[]>([])
  const [step3Subs,    setStep3Subs]       = useState<Step3SubmissionItem[]>([])
  const [step4Subs,    setStep4Subs]       = useState<Step4SubmissionItem[]>([])
  const [step5Subs,    setStep5Subs]       = useState<Step5SubmissionItem[]>([])
  const [step6Subs,    setStep6Subs]       = useState<Step6SubmissionItem[]>([])
  const [unreadReplies,setUnreadReplies]   = useState<UnreadReplyItem[]>([])
  const [revisionRequests, setRevisionRequests] = useState<RevisionRequestItem[]>([])
  const [rsvps,       setRsvps]            = useState<RsvpItem[]>([])
  const [unmarked,    setUnmarked]         = useState<UnmarkedMeetingItem[]>([])
  const [scheduledMentorMeetings, setScheduledMentorMeetings] = useState<ScheduledMentorMeeting[]>([])
  const [loading,     setLoading]          = useState(true)
  const [error,       setError]            = useState<string | null>(null)

  // Attendance dialog (re-uses existing class attendance dialog)
  const [attendanceMeeting, setAttendanceMeeting] = useState<UnmarkedMeetingItem | null>(null)
  const [cancellingMeetingId, setCancellingMeetingId] = useState<string | null>(null)

  async function cancelMeeting(meetingId: string) {
    if (cancellingMeetingId) return
    setCancellingMeetingId(meetingId); setError(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('cancel-meeting', {
        body: { meeting_id: meetingId },
      })
      if (invokeErr) throw new Error(invokeErr.message)
      if (data?.error) throw new Error(data.error)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not cancel the meeting — please try again.')
    } finally {
      setCancellingMeetingId(null)
    }
  }

  async function load() {
    if (!session) return
    setLoading(true)
    setError(null)
    try {
      // Supabase resolves (never throws) on RLS/application errors, so every
      // destructure below must check its own `error` — otherwise a transient
      // failure on any ONE query silently zeroes out that section instead of
      // surfacing a retry, and the panel falsely reports "you're caught up."
      const bail = (err: { message: string } | null, label: string) => {
        if (err) throw new Error(`${label}: ${err.message}`)
      }

      // ── Resolve mentor scope: students they lead-mentor + students in their classes ──
      let scopedStudentIds = new Set<string>()
      let scopedClassIds   = new Set<string>()

      if (role === 'admin' || role === 'office_manager') {
        // Admin/OM: all students + all classes
        const [{ data: profiles, error: profilesErr }, { data: classes, error: classesErr }] = await Promise.all([
          supabase.from('student_profile').select('student_id'),
          supabase.from('class').select('class_id'),
        ])
        bail(profilesErr, 'loading students'); bail(classesErr, 'loading classes')
        for (const p of profiles ?? []) scopedStudentIds.add(p.student_id)
        for (const c of classes ?? []) scopedClassIds.add(c.class_id)
      } else {
        // Mentor/instructor: lead mentees + class students
        const [{ data: leadAssignments, error: leadErr }, { data: classMentors, error: classMentorsErr }] = await Promise.all([
          supabase.from('student_mentor').select('student_id')
            .eq('mentor_id', session.user.id).eq('mentor_role', 'lead').eq('status', 'active'),
          supabase.from('class_mentor').select('class_id').eq('user_id', session.user.id),
        ])
        bail(leadErr, 'loading your mentees'); bail(classMentorsErr, 'loading your classes')
        for (const r of leadAssignments ?? []) scopedStudentIds.add(r.student_id)
        for (const r of classMentors ?? []) scopedClassIds.add(r.class_id)
        if (scopedClassIds.size > 0) {
          const { data: enrollments, error: enrollErr } = await supabase
            .from('class_enrollment')
            .select('student_id')
            .in('class_id', Array.from(scopedClassIds))
            .eq('status', 'active')
          bail(enrollErr, 'loading class rosters')
          for (const e of enrollments ?? []) scopedStudentIds.add(e.student_id)
        }
      }

      // ── 1. Topic submissions awaiting review ──
      const submissionRows: TopicSubmissionItem[] = []
      if (scopedStudentIds.size > 0) {
        const { data: topics, error: topicsErr } = await supabase
          .from('research_topic')
          .select('project_id, student_id, finalist_recommendation_ids, student_thoughts, updated_at')
          .eq('status', 'submitted')
          .in('student_id', Array.from(scopedStudentIds))
          .order('updated_at', { ascending: true })
        bail(topicsErr, 'loading topic submissions')

        if ((topics ?? []).length > 0) {
          // Resolve student names + finalist titles
          const studentIds = (topics ?? []).map(t => t.student_id)
          const allFinalists = Array.from(new Set((topics ?? []).flatMap(t => t.finalist_recommendation_ids ?? [])))
          const [{ data: profiles }, { data: titles }] = await Promise.all([
            supabase.from('student_profile').select('student_id, user_id').in('student_id', studentIds),
            allFinalists.length > 0
              ? supabase.from('topic_recommendation').select('id, title').in('id', allFinalists)
              : Promise.resolve({ data: [] as { id: string; title: string }[] }),
          ])
          const userIds = (profiles ?? []).map(p => p.user_id)
          const { data: users } = userIds.length > 0
            ? await supabase.from('users').select('user_id, first_name, last_name').in('user_id', userIds)
            : { data: [] as any[] }
          const userMap = Object.fromEntries((users ?? []).map(u => [u.user_id, u]))
          const profileMap = Object.fromEntries((profiles ?? []).map(p => [p.student_id, p.user_id]))
          const titleMap = Object.fromEntries((titles ?? []).map(t => [t.id, t.title]))
          for (const t of topics ?? []) {
            const u = userMap[profileMap[t.student_id]]
            const ids = (t.finalist_recommendation_ids ?? []) as string[]
            submissionRows.push({
              project_id:                  t.project_id,
              student_id:                  t.student_id,
              student_name:                u ? `${u.first_name} ${u.last_name}`.trim() : '—',
              finalist_count:              ids.length,
              finalist_recommendation_ids: ids,
              finalist_titles:             ids.map(id => titleMap[id] ?? '(unknown)'),
              student_thoughts_excerpt:    (t.student_thoughts ?? '').slice(0, 120),
              submitted_at:                t.updated_at,
            })
          }
        }
      }
      setSubmissions(submissionRows)

      // ── 1a-bis. Step 2 drafts awaiting review ──
      const step2Rows: Step2DraftItem[] = []
      if (scopedStudentIds.size > 0) {
        const { data: draftRows, error: draftRowsErr } = await supabase
          .from('research_question')
          .select('project_id, student_id, track_committed, draft_payload, updated_at')
          .eq('status', 'submitted')
          .in('student_id', Array.from(scopedStudentIds))
          .order('updated_at', { ascending: true })
        bail(draftRowsErr, 'loading Step 2 drafts')
        if ((draftRows ?? []).length > 0) {
          const sIds = (draftRows ?? []).map(r => r.student_id)
          const { data: profiles } = await supabase
            .from('student_profile').select('student_id, user_id').in('student_id', sIds)
          const userIds = (profiles ?? []).map(p => p.user_id)
          const { data: users } = userIds.length > 0
            ? await supabase.from('users').select('user_id, first_name, last_name').in('user_id', userIds)
            : { data: [] as any[] }
          const userMap    = Object.fromEntries((users    ?? []).map(u => [u.user_id, u]))
          const profileMap = Object.fromEntries((profiles ?? []).map(p => [p.student_id, p.user_id]))
          for (const r of draftRows ?? []) {
            const u = userMap[profileMap[r.student_id]]
            const payload = (r.draft_payload ?? {}) as { question?: string; problem_statement?: string }
            const isEng   = r.track_committed === 'engineering_design'
            const txt     = (isEng ? payload.problem_statement : payload.question)?.trim() ?? ''
            step2Rows.push({
              project_id:   r.project_id,
              student_id:   r.student_id,
              student_name: u ? `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() : '—',
              draft_title:  txt.slice(0, 100) + (txt.length > 100 ? '…' : ''),
              submitted_at: r.updated_at,
              is_eng:       isEng,
            })
          }
        }
      }
      setStep2Drafts(step2Rows)

      // ── 1a-quater. Step 3 background research awaiting review ──
      const step3Rows: Step3SubmissionItem[] = []
      if (scopedStudentIds.size > 0) {
        const { data: bgRows, error: bgRowsErr } = await supabase
          .from('background_research')
          .select('project_id, student_id, payload, updated_at')
          .eq('status', 'submitted')
          .in('student_id', Array.from(scopedStudentIds))
          .order('updated_at', { ascending: true })
        bail(bgRowsErr, 'loading Step 3 submissions')
        if ((bgRows ?? []).length > 0) {
          const sIds = (bgRows ?? []).map(r => r.student_id)
          const { data: profiles } = await supabase
            .from('student_profile').select('student_id, user_id').in('student_id', sIds)
          const userIds = (profiles ?? []).map(p => p.user_id)
          const { data: users } = userIds.length > 0
            ? await supabase.from('users').select('user_id, first_name, last_name').in('user_id', userIds)
            : { data: [] as any[] }
          const userMap    = Object.fromEntries((users    ?? []).map(u => [u.user_id, u]))
          const profileMap = Object.fromEntries((profiles ?? []).map(p => [p.student_id, p.user_id]))
          for (const r of bgRows ?? []) {
            const u = userMap[profileMap[r.student_id]]
            const payload = (r.payload ?? {}) as { refined_question?: string }
            const rq = (payload.refined_question ?? '').trim()
            step3Rows.push({
              project_id:       r.project_id,
              student_id:       r.student_id,
              student_name:     u ? `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() : '—',
              refined_question: rq.slice(0, 100) + (rq.length > 100 ? '…' : ''),
              submitted_at:     r.updated_at,
            })
          }
        }
      }
      setStep3Subs(step3Rows)

      // ── 1a-quinquies. Step 4 hypothesis awaiting review ──
      const step4Rows: Step4SubmissionItem[] = []
      if (scopedStudentIds.size > 0) {
        const { data: hdRows, error: hdRowsErr } = await supabase
          .from('hypothesis_design')
          .select('project_id, student_id, payload, updated_at')
          .eq('status', 'submitted')
          .in('student_id', Array.from(scopedStudentIds))
          .order('updated_at', { ascending: true })
        bail(hdRowsErr, 'loading Step 4 submissions')
        if ((hdRows ?? []).length > 0) {
          const sIds = (hdRows ?? []).map(r => r.student_id)
          const { data: profiles } = await supabase
            .from('student_profile').select('student_id, user_id').in('student_id', sIds)
          const userIds = (profiles ?? []).map(p => p.user_id)
          const { data: users } = userIds.length > 0
            ? await supabase.from('users').select('user_id, first_name, last_name').in('user_id', userIds)
            : { data: [] as any[] }
          const userMap    = Object.fromEntries((users    ?? []).map(u => [u.user_id, u]))
          const profileMap = Object.fromEntries((profiles ?? []).map(p => [p.student_id, p.user_id]))
          for (const r of hdRows ?? []) {
            const u = userMap[profileMap[r.student_id]]
            const payload = (r.payload ?? {}) as { blocks?: Array<{ hypothesis?: string }>; hypothesis?: string }
            const hyp = (payload.blocks?.[0]?.hypothesis ?? payload.hypothesis ?? '').trim()
            step4Rows.push({
              project_id:   r.project_id,
              student_id:   r.student_id,
              student_name: u ? `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() : '—',
              hypothesis:   hyp.slice(0, 100) + (hyp.length > 100 ? '…' : ''),
              submitted_at: r.updated_at,
            })
          }
        }
      }
      setStep4Subs(step4Rows)

      // ── 1a-sexies. Step 5 experimental design awaiting review ──
      const step5Rows: Step5SubmissionItem[] = []
      if (scopedStudentIds.size > 0) {
        const { data: edRows, error: edRowsErr } = await supabase
          .from('experiment_design')
          .select('project_id, student_id, track, payload, updated_at')
          .eq('status', 'submitted')
          .in('student_id', Array.from(scopedStudentIds))
          .order('updated_at', { ascending: true })
        bail(edRowsErr, 'loading Step 5 submissions')
        if ((edRows ?? []).length > 0) {
          const sIds = (edRows ?? []).map(r => r.student_id)
          const { data: profiles } = await supabase
            .from('student_profile').select('student_id, user_id').in('student_id', sIds)
          const userIds = (profiles ?? []).map(p => p.user_id)
          const { data: users } = userIds.length > 0
            ? await supabase.from('users').select('user_id, first_name, last_name').in('user_id', userIds)
            : { data: [] as any[] }
          const userMap    = Object.fromEntries((users    ?? []).map(u => [u.user_id, u]))
          const profileMap = Object.fromEntries((profiles ?? []).map(p => [p.student_id, p.user_id]))
          for (const r of edRows ?? []) {
            const u = userMap[profileMap[r.student_id]]
            const payload = (r.payload ?? {}) as { design_type?: string; design_description?: string }
            const isEng   = r.track === 'engineering_design'
            const txt     = (isEng ? payload.design_description : payload.design_type)?.trim() ?? ''
            step5Rows.push({
              project_id:   r.project_id,
              student_id:   r.student_id,
              student_name: u ? `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() : '—',
              design_title: txt.slice(0, 100) + (txt.length > 100 ? '…' : ''),
              submitted_at: r.updated_at,
              is_eng:       isEng,
            })
          }
        }
      }
      setStep5Subs(step5Rows)

      // ── 1a-septies. Step 6 preliminary observation awaiting review ──
      const step6Rows: Step6SubmissionItem[] = []
      if (scopedStudentIds.size > 0) {
        const { data: erRows, error: erRowsErr } = await supabase
          .from('experiment_record')
          .select('project_id, student_id, payload, updated_at')
          .eq('status', 'submitted')
          .in('student_id', Array.from(scopedStudentIds))
          .order('updated_at', { ascending: true })
        bail(erRowsErr, 'loading Step 6 submissions')
        if ((erRows ?? []).length > 0) {
          const sIds = (erRows ?? []).map(r => r.student_id)
          const { data: profiles } = await supabase
            .from('student_profile').select('student_id, user_id').in('student_id', sIds)
          const userIds = (profiles ?? []).map(p => p.user_id)
          const { data: users } = userIds.length > 0
            ? await supabase.from('users').select('user_id, first_name, last_name').in('user_id', userIds)
            : { data: [] as any[] }
          const userMap    = Object.fromEntries((users    ?? []).map(u => [u.user_id, u]))
          const profileMap = Object.fromEntries((profiles ?? []).map(p => [p.student_id, p.user_id]))
          for (const r of erRows ?? []) {
            const u = userMap[profileMap[r.student_id]]
            const txt = ((r.payload ?? {}) as { preliminary_observation?: string }).preliminary_observation?.trim() ?? ''
            step6Rows.push({
              project_id:   r.project_id,
              student_id:   r.student_id,
              student_name: u ? `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() : '—',
              observation:  txt.slice(0, 100) + (txt.length > 100 ? '…' : ''),
              submitted_at: r.updated_at,
            })
          }
        }
      }
      setStep6Subs(step6Rows)

      // ── 1a-tris. Student replies awaiting mentor follow-up ──
      // A project has an unread reply when the most recent thread entry is a
      // student_message — nothing from staff came after it.
      //
      // "Thread entry" has to include approve and comment, not just message.
      // A mentor answering a student almost always does it BY approving or
      // sending back, with the answer in the note; a bare 'message' is the
      // rare case. Counting only messages meant an approval never cleared the
      // item, so a mentor who had already replied kept being told to reply —
      // and the student looked like they were waiting when they were not.
      const unreadRows: UnreadReplyItem[] = []
      if (scopedStudentIds.size > 0) {
        const { data: scopedProjects, error: scopedProjectsErr } = await supabase
          .from('project')
          .select('project_id, student_id')
          .in('student_id', Array.from(scopedStudentIds))
        bail(scopedProjectsErr, 'loading projects')
        const scopedProjectIds = (scopedProjects ?? []).map(p => p.project_id)
        if (scopedProjectIds.length > 0) {
          const { data: msgs, error: msgsErr } = await supabase
            .from('mentor_review_log')
            .select('project_id, action, note, acted_at, reviewer_id')
            .in('project_id', scopedProjectIds)
            .in('action', ['message', 'student_message', 'approve', 'comment'])
            .order('acted_at', { ascending: false })
          bail(msgsErr, 'loading replies')

          // Take latest per project; keep only those where it's a student message.
          const latestByProject = new Map<string, any>()
          for (const m of msgs ?? []) {
            if (!latestByProject.has(m.project_id)) latestByProject.set(m.project_id, m)
          }
          const unreadCandidates = Array.from(latestByProject.values())
            .filter(m => m.action === 'student_message')

          if (unreadCandidates.length > 0) {
            const projMap = Object.fromEntries((scopedProjects ?? []).map(p => [p.project_id, p.student_id]))
            const sIds    = Array.from(new Set(unreadCandidates.map(m => projMap[m.project_id]).filter(Boolean) as string[]))
            const { data: profiles } = await supabase
              .from('student_profile').select('student_id, user_id').in('student_id', sIds)
            const userIds = (profiles ?? []).map(p => p.user_id)
            const { data: users } = userIds.length > 0
              ? await supabase.from('users').select('user_id, first_name, last_name').in('user_id', userIds)
              : { data: [] as any[] }
            const userMap    = Object.fromEntries((users    ?? []).map(u => [u.user_id, u]))
            const profileMap = Object.fromEntries((profiles ?? []).map(p => [p.student_id, p.user_id]))
            for (const m of unreadCandidates) {
              const studentId = projMap[m.project_id]
              const u         = userMap[profileMap[studentId]]
              const note      = (m.note ?? '').trim()
              unreadRows.push({
                project_id:   m.project_id,
                student_id:   studentId,
                student_name: u ? `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() : '—',
                excerpt:      note.slice(0, 120) + (note.length > 120 ? '…' : ''),
                arrived_at:   m.acted_at,
              })
            }
            unreadRows.sort((a, b) => a.arrived_at.localeCompare(b.arrived_at))
          }
        }
      }
      setUnreadReplies(unreadRows)

      // ── 1a. Pending revision requests (mentor-permission flow) ──
      const revisionRows: RevisionRequestItem[] = []
      if (scopedStudentIds.size > 0) {
        const { data: pendingRevs, error: pendingRevsErr } = await supabase
          .from('revision_request')
          .select('id, project_id, student_id, step_number, reason, requested_at')
          .eq('status', 'pending')
          .in('student_id', Array.from(scopedStudentIds))
          .order('requested_at', { ascending: true })
        bail(pendingRevsErr, 'loading revision requests')
        if ((pendingRevs ?? []).length > 0) {
          const studentIds = (pendingRevs ?? []).map(r => r.student_id)
          const { data: profiles } = await supabase
            .from('student_profile').select('student_id, user_id').in('student_id', studentIds)
          const userIds = (profiles ?? []).map(p => p.user_id)
          const { data: users } = userIds.length > 0
            ? await supabase.from('users').select('user_id, first_name, last_name').in('user_id', userIds)
            : { data: [] as any[] }
          const profileMap = Object.fromEntries((profiles ?? []).map(p => [p.student_id, p.user_id]))
          const userMap    = Object.fromEntries((users    ?? []).map(u => [u.user_id, u]))
          for (const r of pendingRevs ?? []) {
            const u = userMap[profileMap[r.student_id]]
            revisionRows.push({
              request_id:   r.id,
              project_id:   r.project_id,
              student_id:   r.student_id,
              student_name: u ? `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() : '—',
              step_number:  r.step_number,
              reason:       r.reason,
              requested_at: r.requested_at,
            })
          }
        }
      }
      setRevisionRequests(revisionRows)

      // ── 1b. Per-student mentor meetings — requested (awaiting a time) or
      //         scheduled by the office. Both are "pending" until held/cancelled.
      const mentorMeetingRows: ScheduledMentorMeeting[] = []
      if (scopedStudentIds.size > 0) {
        const { data: scheduled, error: scheduledErr } = await supabase
          .from('meeting')
          .select('meeting_id, project_id, student_id, scheduled_at, scheduled_note, request_note, kind, status')
          .in('status', ['requested', 'scheduled'])
          .in('student_id', Array.from(scopedStudentIds))
          .order('scheduled_at', { ascending: true, nullsFirst: true })
        bail(scheduledErr, 'loading meetings')
        if ((scheduled ?? []).length > 0) {
          const sIds   = (scheduled ?? []).map((t: any) => t.student_id)
          const { data: profiles } = await supabase
            .from('student_profile').select('student_id, user_id').in('student_id', sIds)
          const userIds = (profiles ?? []).map(p => p.user_id)
          const { data: users } = userIds.length > 0
            ? await supabase.from('users').select('user_id, first_name, last_name').in('user_id', userIds)
            : { data: [] as any[] }
          const userMap    = Object.fromEntries((users    ?? []).map(u => [u.user_id, u]))
          const profileMap = Object.fromEntries((profiles ?? []).map(p => [p.student_id, p.user_id]))
          for (const t of (scheduled ?? []) as any[]) {
            const u = userMap[profileMap[t.student_id]]
            mentorMeetingRows.push({
              meeting_id:   t.meeting_id,
              project_id:   t.project_id,
              student_id:   t.student_id,
              student_name: u ? `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() : '—',
              scheduled_at: t.scheduled_at ?? null,
              status:       t.status,
              note:         t.scheduled_note ?? t.request_note ?? null,
              kind:         t.kind,
            })
          }
        }
      }
      setScheduledMentorMeetings(mentorMeetingRows)

      // ── 2. Office-hour RSVPs awaiting outcome (past office hours, hosted by me, status=planning) ──
      const rsvpRows: RsvpItem[] = []
      if (role !== 'admin' && role !== 'office_manager') {
        // Only host-mentor sees this. Admin/OM hide.
        const nowIso = new Date().toISOString()
        const { data: pastOH, error: pastOHErr } = await supabase
          .from('class_meeting')
          .select('meeting_id, class_id, scheduled_at')
          .eq('host_mentor_id', session.user.id)
          .eq('kind', 'office_hours')
          .neq('status', 'cancelled')
          .lt('scheduled_at', nowIso)
        bail(pastOHErr, 'loading office hours')
        if ((pastOH ?? []).length > 0) {
          const meetingIds = (pastOH ?? []).map(m => m.meeting_id)
          const classIds   = Array.from(new Set((pastOH ?? []).map(m => m.class_id)))
          const [{ data: planningRows, error: planningErr }, { data: classes, error: rsvpClassesErr }] = await Promise.all([
            supabase.from('class_meeting_attendance')
              .select('meeting_id')
              .in('meeting_id', meetingIds)
              .eq('status', 'planning'),
            supabase.from('class').select('class_id, name').in('class_id', classIds),
          ])
          bail(planningErr, 'loading RSVPs'); bail(rsvpClassesErr, 'loading classes')
          const classMap = Object.fromEntries((classes ?? []).map(c => [c.class_id, c.name]))
          const planningByMeeting: Record<string, number> = {}
          for (const p of planningRows ?? []) {
            planningByMeeting[p.meeting_id] = (planningByMeeting[p.meeting_id] ?? 0) + 1
          }
          for (const m of pastOH ?? []) {
            const count = planningByMeeting[m.meeting_id] ?? 0
            if (count > 0) {
              rsvpRows.push({
                meeting_id:   m.meeting_id,
                class_id:     m.class_id,
                class_name:   classMap[m.class_id] ?? '—',
                scheduled_at: m.scheduled_at,
                rsvp_count:   count,
              })
            }
          }
        }
      }
      setRsvps(rsvpRows)

      // ── 3. Past meetings without any attendance recorded ──
      const unmarkedRows: UnmarkedMeetingItem[] = []
      if (scopedClassIds.size > 0) {
        const nowIso = new Date().toISOString()
        const { data: meetings, error: meetingsErr } = await supabase
          .from('class_meeting')
          .select('meeting_id, class_id, kind, scheduled_at')
          .in('class_id', Array.from(scopedClassIds))
          .eq('status', 'scheduled')
          .lt('scheduled_at', nowIso)
        bail(meetingsErr, 'loading meetings')
        if ((meetings ?? []).length > 0) {
          const meetingIds = (meetings ?? []).map(m => m.meeting_id)
          const classIds   = Array.from(new Set((meetings ?? []).map(m => m.class_id)))
          const [{ data: attendanceRows, error: attendanceErr }, { data: classes, error: unmarkedClassesErr }, { data: enrollments, error: unmarkedEnrollErr }] = await Promise.all([
            supabase.from('class_meeting_attendance')
              .select('meeting_id, status')
              .in('meeting_id', meetingIds),
            supabase.from('class').select('class_id, name').in('class_id', classIds),
            supabase.from('class_enrollment').select('class_id').in('class_id', classIds).eq('status', 'active'),
          ])
          bail(attendanceErr, 'loading attendance'); bail(unmarkedClassesErr, 'loading classes'); bail(unmarkedEnrollErr, 'loading rosters')
          const classMap = Object.fromEntries((classes ?? []).map(c => [c.class_id, c.name]))
          const enrollCountByClass: Record<string, number> = {}
          for (const e of enrollments ?? []) {
            enrollCountByClass[e.class_id] = (enrollCountByClass[e.class_id] ?? 0) + 1
          }
          // A meeting is "unmarked" if it has zero attendance rows with status in
          // (present/absent/excused/no_show). Planning rows alone don't count as
          // marked attendance for group meetings.
          const markedMeetings = new Set<string>()
          for (const a of attendanceRows ?? []) {
            if (a.status !== 'planning') markedMeetings.add(a.meeting_id)
          }
          for (const m of meetings ?? []) {
            if (!markedMeetings.has(m.meeting_id)) {
              unmarkedRows.push({
                meeting_id:     m.meeting_id,
                class_id:       m.class_id,
                class_name:     classMap[m.class_id] ?? '—',
                kind:           m.kind,
                scheduled_at:   m.scheduled_at,
                enrolled_count: enrollCountByClass[m.class_id] ?? 0,
              })
            }
          }
          unmarkedRows.sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at))
        }
      }
      setUnmarked(unmarkedRows)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() }, [session, role])

  // Every section that can render MUST be counted here — the whole list is
  // hidden behind `totalCount === 0` ("You're caught up"), so a section missing
  // from this sum is invisible whenever it's the only thing pending. Step 4 was
  // omitted when it shipped; Step 5 would have repeated it.
  const totalCount = submissions.length + step2Drafts.length + step3Subs.length + step4Subs.length
    + step5Subs.length + step6Subs.length + unreadReplies.length + revisionRequests.length + rsvps.length
    + unmarked.length + scheduledMentorMeetings.length

  return (
    <Box sx={{
      mb: 3, p: 2.5, borderRadius: 2,
      border: '1px solid', borderColor: totalCount > 0 ? 'primary.light' : 'divider',
      bgcolor: totalCount > 0 ? 'rgba(15,43,76,0.025)' : 'background.paper',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>Action Items</Typography>
        <Badge badgeContent={totalCount} color="primary" />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
      ) : totalCount === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
          You're caught up — nothing waiting on you right now.
        </Typography>
      ) : (
        <>
          {/* Step 2 drafts awaiting review */}
          {step2Drafts.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <SectionHeader
                icon={<AssignmentIcon fontSize="small" sx={{ color: 'error.main' }} />}
                title="Step 2 drafts awaiting your review"
                count={step2Drafts.length}
                color="error"
              />
              {step2Drafts.map(s => (
                <ActionRow
                  key={s.project_id}
                  primary={
                    <>
                      {s.student_name}{' '}
                      <Typography component="span" variant="caption" color="text.secondary">
                        — {s.is_eng ? 'problem statement' : 'research question'} draft · {timeSince(s.submitted_at)}
                      </Typography>
                    </>
                  }
                  secondary={s.draft_title ? <em>"{s.draft_title}"</em> : undefined}
                  action="Review"
                  onAction={() => navigate(`/review/${s.project_id}`)}
                />
              ))}
            </Box>
          )}

          {/* Step 3 background research awaiting review */}
          {step3Subs.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <SectionHeader
                icon={<AssignmentIcon fontSize="small" sx={{ color: 'error.main' }} />}
                title="Step 3 background research awaiting your review"
                count={step3Subs.length}
                color="error"
              />
              {step3Subs.map(s => (
                <ActionRow
                  key={s.project_id}
                  primary={
                    <>
                      {s.student_name}{' '}
                      <Typography component="span" variant="caption" color="text.secondary">
                        — background research · {timeSince(s.submitted_at)}
                      </Typography>
                    </>
                  }
                  secondary={s.refined_question ? <em>"{s.refined_question}"</em> : undefined}
                  action="Review"
                  onAction={() => navigate(`/review/${s.project_id}`)}
                />
              ))}
            </Box>
          )}

          {/* Step 4 hypothesis awaiting review */}
          {step4Subs.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <SectionHeader
                icon={<AssignmentIcon fontSize="small" sx={{ color: 'error.main' }} />}
                title="Step 4 hypothesis awaiting your review"
                count={step4Subs.length}
                color="error"
              />
              {step4Subs.map(s => (
                <ActionRow
                  key={s.project_id}
                  primary={
                    <>
                      {s.student_name}{' '}
                      <Typography component="span" variant="caption" color="text.secondary">
                        — hypothesis · {timeSince(s.submitted_at)}
                      </Typography>
                    </>
                  }
                  secondary={s.hypothesis ? <em>"{s.hypothesis}"</em> : undefined}
                  action="Review"
                  onAction={() => navigate(`/review/${s.project_id}`)}
                />
              ))}
            </Box>
          )}

          {/* Step 5 experimental design awaiting review */}
          {step5Subs.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <SectionHeader
                icon={<AssignmentIcon fontSize="small" sx={{ color: 'error.main' }} />}
                title="Step 5 experimental design awaiting your review"
                count={step5Subs.length}
                color="error"
              />
              {step5Subs.map(s => (
                <ActionRow
                  key={s.project_id}
                  primary={
                    <>
                      {s.student_name}{' '}
                      <Typography component="span" variant="caption" color="text.secondary">
                        — {s.is_eng ? 'design document' : 'experimental design'} · {timeSince(s.submitted_at)}
                      </Typography>
                    </>
                  }
                  secondary={s.design_title ? <em>"{s.design_title}"</em> : undefined}
                  action="Review"
                  onAction={() => navigate(`/review/${s.project_id}`)}
                />
              ))}
            </Box>
          )}

          {/* Step 6 preliminary observation awaiting review */}
          {step6Subs.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <SectionHeader
                icon={<AssignmentIcon fontSize="small" sx={{ color: 'error.main' }} />}
                title="Step 6 preliminary observation awaiting your review"
                count={step6Subs.length}
                color="error"
              />
              {step6Subs.map(s => (
                <ActionRow
                  key={s.project_id}
                  primary={
                    <>
                      {s.student_name}{' '}
                      <Typography component="span" variant="caption" color="text.secondary">
                        — experiment · {timeSince(s.submitted_at)}
                      </Typography>
                    </>
                  }
                  secondary={s.observation ? <em>"{s.observation}"</em> : undefined}
                  action="Review"
                  onAction={() => navigate(`/review/${s.project_id}`)}
                />
              ))}
            </Box>
          )}

          {/* Student replies awaiting follow-up */}
          {unreadReplies.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <SectionHeader
                icon={<HowToRegIcon fontSize="small" sx={{ color: 'info.main' }} />}
                title="Student replies awaiting your follow-up"
                count={unreadReplies.length}
                color="info"
              />
              {unreadReplies.map(r => (
                <ActionRow
                  key={r.project_id}
                  primary={
                    <>
                      {r.student_name}{' '}
                      <Typography component="span" variant="caption" color="text.secondary">
                        — replied {timeSince(r.arrived_at)}
                      </Typography>
                    </>
                  }
                  secondary={r.excerpt ? <em>"{r.excerpt}"</em> : undefined}
                  action="Open"
                  onAction={() => navigate(`/review/${r.project_id}`)}
                />
              ))}
            </Box>
          )}

          {/* Revision requests — high priority, surfaced before topic submissions */}
          {revisionRequests.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <SectionHeader
                icon={<RestartAltIcon fontSize="small" sx={{ color: 'warning.main' }} />}
                title="Revision requests awaiting your decision"
                count={revisionRequests.length}
                color="warning"
              />
              {revisionRequests.map(r => {
                const excerpt = r.reason.length > 120 ? `${r.reason.slice(0, 120)}…` : r.reason
                return (
                  <ActionRow
                    key={r.request_id}
                    primary={
                      <>
                        {r.student_name}{' '}
                        <Typography component="span" variant="caption" color="text.secondary">
                          — wants to revise Step {r.step_number} · {timeSince(r.requested_at)}
                        </Typography>
                      </>
                    }
                    secondary={<em>"{excerpt}"</em>}
                    action="Review"
                    onAction={() => navigate(`/review/${r.project_id}`)}
                  />
                )
              })}
            </Box>
          )}

          {/* Topic submissions */}
          {submissions.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <SectionHeader
                icon={<AssignmentIcon fontSize="small" sx={{ color: 'error.main' }} />}
                title="Topic submissions awaiting your review"
                count={submissions.length}
                color="error"
              />
              {submissions.map(s => (
                <ActionRow
                  key={s.project_id}
                  primary={
                    <>
                      {s.student_name}{' '}
                      <Typography component="span" variant="caption" color="text.secondary">
                        — {s.finalist_count} finalist{s.finalist_count !== 1 ? 's' : ''} · submitted {timeSince(s.submitted_at)}
                      </Typography>
                    </>
                  }
                  secondary={
                    <>
                      {s.finalist_titles.join(' · ')}
                      {s.student_thoughts_excerpt && <> — <em>"{s.student_thoughts_excerpt}{s.student_thoughts_excerpt.length >= 120 ? '…' : ''}"</em></>}
                    </>
                  }
                  action="Review"
                  onAction={() => navigate(`/review/${s.project_id}`)}
                />
              ))}
            </Box>
          )}

          {/* Pending per-student mentor meetings — requested or scheduled. Each
              can be opened (for review meetings) or cancelled when no longer needed. */}
          {scheduledMentorMeetings.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <SectionHeader
                icon={<EventAvailableIcon fontSize="small" sx={{ color: 'info.main' }} />}
                title="Pending meetings"
                count={scheduledMentorMeetings.length}
                color="info"
              />
              {scheduledMentorMeetings.map(m => {
                const kindLabel = m.kind === 'kickoff' ? 'Kickoff'
                                : m.kind === 'step1_review' ? 'Step 1 review'
                                : m.kind === 'parent_requested' ? 'Parent-requested'
                                : 'Meeting'
                const when = m.status === 'scheduled' && m.scheduled_at
                  ? fmtDateTime(m.scheduled_at)
                  : 'requested — awaiting a time'
                return (
                  <Box key={m.meeting_id} sx={{
                    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                    gap: 1.5, py: 1.25, borderTop: '1px solid', borderColor: 'divider',
                    '&:first-of-type': { borderTop: 'none' },
                  }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {kindLabel} · {m.student_name}{' '}
                        <Typography component="span" variant="caption" color="text.secondary">— {when}</Typography>
                      </Typography>
                      {m.note && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          <em>"{m.note}"</em>
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                      {m.project_id && (
                        <Button size="small" variant="outlined" onClick={() => navigate(`/review/${m.project_id}`)}>
                          Open review
                        </Button>
                      )}
                      <Button
                        size="small" variant="text" color="warning"
                        disabled={cancellingMeetingId === m.meeting_id}
                        onClick={() => cancelMeeting(m.meeting_id)}
                      >
                        {cancellingMeetingId === m.meeting_id ? 'Cancelling…' : 'Cancel'}
                      </Button>
                    </Box>
                  </Box>
                )
              })}
            </Box>
          )}

          {/* Office-hour RSVPs */}
          {rsvps.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <SectionHeader
                icon={<HowToRegIcon fontSize="small" sx={{ color: 'warning.main' }} />}
                title="Office-hour RSVPs awaiting outcome"
                count={rsvps.length}
                color="warning"
              />
              {rsvps.map(r => (
                <ActionRow
                  key={r.meeting_id}
                  primary={r.class_name}
                  secondary={`${r.rsvp_count} RSVP${r.rsvp_count !== 1 ? 's' : ''} · ${fmtDateTime(r.scheduled_at)}`}
                  action="Mark held"
                  onAction={() => setAttendanceMeeting({
                    meeting_id: r.meeting_id, class_id: r.class_id, class_name: r.class_name,
                    kind: 'office_hours', scheduled_at: r.scheduled_at, enrolled_count: 0,
                  })}
                />
              ))}
            </Box>
          )}

          {/* Past meetings without attendance */}
          {unmarked.length > 0 && (
            <Box>
              <SectionHeader
                icon={<EventAvailableIcon fontSize="small" sx={{ color: 'warning.main' }} />}
                title="Past meetings without attendance"
                count={unmarked.length}
                color="warning"
              />
              {unmarked.map(m => (
                <ActionRow
                  key={m.meeting_id}
                  primary={m.class_name}
                  secondary={`${m.kind === 'office_hours' ? 'Office hour' : 'Group meeting'} · ${fmtDateTime(m.scheduled_at)}`}
                  action="Mark attendance"
                  onAction={() => setAttendanceMeeting(m)}
                />
              ))}
            </Box>
          )}
        </>
      )}

      {/* Attendance dialog (re-uses class attendance UI) */}
      <AttendanceDialog
        open={!!attendanceMeeting}
        meetingId={attendanceMeeting?.meeting_id ?? null}
        classId={attendanceMeeting?.class_id ?? null}
        meetingKind={attendanceMeeting?.kind ?? null}
        meetingLabel={attendanceMeeting
          ? `${attendanceMeeting.class_name} — ${fmtDateTime(attendanceMeeting.scheduled_at)}`
          : ''}
        onClose={() => setAttendanceMeeting(null)}
        onSaved={() => { setAttendanceMeeting(null); load() }}
      />
    </Box>
  )
}
